import { HashMap } from '@/types/Common';
import { AppliedFoodLabel, EzShipCartResp } from '@/types/EzShip';
import { FoodLabel } from '@/types/FoodLabel';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type FoodLabelStatus = {
  isLoading: boolean;
  isComplete: boolean;
};

type EzShipConfig = {
  isNewEzShip: boolean;
  ezShipId?: number | string;
};

type ExistingAppliedLables = {
  allLabels: FoodLabel[];
  appliedLables: AppliedFoodLabel[];
};

type EzShipSlice = {
  currentEzShip: EzShipCartResp | undefined;
  foodLabel?: FoodLabelStatus;
  config?: EzShipConfig;
  existingLables?: ExistingAppliedLables;
  isRemoveFoodLabelInProgress?: boolean;
  isFetchingEzShipCartItems?: boolean;
  isRemovingEzShipCartItem?: boolean;

  ezShipTitle?: string;
  action: Action;
};

type ActionName = 'addToEzShip' | 'updateEzShipCartQuantity' | 'removeEzShipFromCart' | '';

type Action = {
  name: ActionName;
  data: HashMap;
};

const initialState = {
  currentEzShip: undefined,
  foodLabel: undefined,
  config: {
    isNewEzShip: false,
    ezShipId: undefined,
  },
  existingLables: {
    allLabels: [],
    appliedLables: [],
  },

  isRemoveFoodLabelInProgress: undefined,
  isFetchingEzShipCartItems: undefined,
  isRemovingEzShipCartItem: undefined,
  action: { name: '', data: {} },
  ezShipTitle: '',
} as EzShipSlice;

const ezShipSlice = createSlice({
  name: 'ezShipSlice',
  initialState,
  reducers: {
    setCurrentEzShip(state, { payload: ezship }: PayloadAction<EzShipCartResp | undefined>) {
      state.currentEzShip = ezship;
    },

    setFoodLabelStatus(state, { payload }: PayloadAction<FoodLabelStatus | undefined>) {
      state.foodLabel = payload;
    },

    setEzShipConfig(state, { payload }: PayloadAction<EzShipConfig | undefined>) {
      state.config = payload;
    },

    setExistingLables(state, { payload }: PayloadAction<ExistingAppliedLables | undefined>) {
      state.existingLables = payload;
    },

    setFoodLabelRemovalStatus(state, { payload }: PayloadAction<boolean | undefined>) {
      state.isRemoveFoodLabelInProgress = payload;
    },

    setGetEzShipCartItemsStatus(state, { payload }: PayloadAction<boolean | undefined>) {
      state.isFetchingEzShipCartItems = payload;
    },

    setRemoveEzShipCartItemStatus(state, { payload }: PayloadAction<boolean | undefined>) {
      state.isRemovingEzShipCartItem = payload;
    },
    setEzShipTitle(state, { payload }: PayloadAction<string | undefined>) {
      state.ezShipTitle = payload;
    },
    setAction(state, { payload }: PayloadAction<{ name: ActionName; id?: keyof HashMap }>) {
      const { name, id } = payload;
      state.action.name = name;

      if (id) {
        state.action.data[id] = id;
      }
    },
    removeAction(state) {
      state.action.name = '';
      state.action.data = {};
    },
    removeActionData(state, { payload }: PayloadAction<{ id: string }>) {
      if (state.action.data[payload.id]) {
        delete state.action.data[payload.id];
      }
    },
  },
});

export const {
  setCurrentEzShip,
  setGetEzShipCartItemsStatus,
  setFoodLabelStatus,
  setExistingLables,
  setEzShipConfig,
  setFoodLabelRemovalStatus,
  setRemoveEzShipCartItemStatus,
  setEzShipTitle,
  setAction,
  removeAction,
  removeActionData,
} = ezShipSlice.actions;
export default ezShipSlice;
